import { Icon24Spinner } from '@vkontakte/icons';
import classNames from 'clsx';
import { xray } from 'lib/xray';
import React, { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IS_CHOSEN_PUBLIC_FILE } from 'reactApp/appHelpers/configHelpers';
import { useNormalizedOvidius } from 'reactApp/hooks/useNormalizedOvidius';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { PdfViewer } from 'reactApp/ui/PdfViewer/PdfViewer';
import { NormalizedPdfHeader } from 'reactApp/ui/ReactViewer/NormalizedConvertedViewerPdf/NormalizedPdfHeader/NormalizedPdfHeader';
import { Spinner } from 'reactApp/ui/Spinner/Spinner';
import { sendLoadTime } from 'reactApp/utils/sendLoadTime';

import styles from './NormalizedConvertedViewerPdf.css';

const onError = (error: any): void => {
    xray.send('norm_pdf_view_err', {
        rlog: 'cloud_norm_pdf_err',
        rlog_message: { error },
    });
};

interface Props {
    storage?: EStorageType | null;
    itemId: string;
    setContentForPrint: (content: ArrayBuffer[] | null) => void;
    renderPdfFallback: () => ReactElement | null;
}

export const NormalizedConvertedViewerPdf: FC<Props> = ({ storage, itemId, setContentForPrint, renderPdfFallback }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isLoadSuccess, setIsLoadSuccess] = useState(false);
    const mountTime = useRef<number>(0);

    const { content, isLoad, partsAmount, onIncreaseScaleClick, onDecreaseScaleClick, scale, isError } = useNormalizedOvidius(
        itemId,
        setContentForPrint,
        storage
    );

    useEffect(() => {
        xray.send('norm_pdf_view_open');
        mountTime.current = Date.now();
    }, []);

    const onLoadSuccessCallback = useCallback(() => {
        if (!isLoadSuccess) {
            sendLoadTime(mountTime.current, 'norm_ovidius_load_time');
        }

        setIsLoadSuccess(true);
    }, [isLoadSuccess]);

    const contentOffsets = useMemo(() => [10, 64], []);

    if (isError) {
        return renderPdfFallback();
    }

    if (!isLoad) {
        return <Spinner />;
    }

    const loadingPagesCondition = partsAmount !== 0 && partsAmount > content.length && isLoadSuccess;

    return (
        <div className={classNames(styles.container, { [styles.singlePublic]: IS_CHOSEN_PUBLIC_FILE })}>
            <NormalizedPdfHeader onIncreaseScaleClick={onIncreaseScaleClick} onDecreaseScaleClick={onDecreaseScaleClick} scale={scale} />

            <div ref={contentRef} className={styles.content}>
                <div>
                    {!!content.length &&
                        content.map((partContent, index) => (
                            <PdfViewer
                                key={index}
                                documentScale={scale}
                                content={partContent}
                                onError={onError}
                                renderTextLayer={true}
                                onLoadSuccessCallback={onLoadSuccessCallback}
                                scrollWrapperRef={contentRef}
                                contentOffsets={contentOffsets}
                            />
                        ))}
                </div>

                {loadingPagesCondition && (
                    <div className={styles.loadingBlock}>
                        <Icon24Spinner className={styles.spinnerIcon} height={30} />
                        <p className={styles.loadingBlockText}>Загрузка страниц</p>
                    </div>
                )}
            </div>
        </div>
    );
};

NormalizedConvertedViewerPdf.displayName = 'NormalizedConvertedViewerPdf';
